/**
 * Tiny typed wrapper over the Express API.
 *
 * In development: Vite proxies /api, /auth, /healthz to localhost:3000.
 * In production: frontend and API are on different domains, so we prefix
 * every URL with VITE_API_BASE (e.g. https://api.mailmeback.pro).
 */

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';
const u = (path: string): string => `${API_BASE}${path}`;

export interface ConnectStatus {
  user: { id: string; email: string; tenantId: string | null } | null;
  microsoft: { connected: boolean; primarySmtp?: string; accountId?: string };
  hubspot: Array<{ hubId: string; scopes: string[]; expiresAt: string }>;
}

export interface BackfillJob {
  id: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';
  folder: 'all' | 'inbox' | 'sentitems';
  hubId: string;
  dateFrom: string | null;
  dateTo: string | null;
  processed: number;
  imported: number;
  skipped: number;
  lastError: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface StartJobInput {
  hubId: string;
  folder?: 'all' | 'inbox' | 'sentitems';
  dateFrom?: string;
  dateTo?: string;
  filterConfig?: Record<string, unknown>;
  shouldEnrich?: boolean;
}

async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(u(path), {
    credentials: 'include',
    headers: { Accept: 'application/json', ...(init?.body ? { 'Content-Type': 'application/json' } : {}) },
    ...init,
  });
  if (!r.ok) {
    let body: unknown;
    try {
      body = await r.json();
    } catch {
      body = await r.text().catch(() => undefined);
    }
    throw new Error(`API ${r.status} ${path}: ${JSON.stringify(body)}`);
  }
  return (await r.json()) as T;
}

export function getStatus(): Promise<ConnectStatus> {
  return jsonFetch<ConnectStatus>('/api/connect/status');
}

export function startMicrosoftConnect(): void {
  window.location.href = u('/auth/microsoft');
}

export function startHubspotConnect(): void {
  window.location.href = u('/auth/hubspot');
}

export function startJob(input: StartJobInput): Promise<{ jobId: string; status: string }> {
  return jsonFetch('/api/jobs', { method: 'POST', body: JSON.stringify(input) });
}

export function listJobs(): Promise<{ jobs: BackfillJob[] }> {
  return jsonFetch('/api/jobs');
}

export function getJob(id: string): Promise<BackfillJob> {
  return jsonFetch(`/api/jobs/${id}`);
}

export function subscribeJobEvents(
  id: string,
  onEvent: (data: unknown) => void
): () => void {
  const src = new EventSource(u(`/api/jobs/${id}/events`), { withCredentials: true });
  src.onmessage = (e) => {
    try {
      onEvent(JSON.parse(e.data));
    } catch {
      /* ignore */
    }
  };
  return () => src.close();
}

export async function logout(): Promise<void> {
  await fetch(u('/auth/logout'), { method: 'POST', credentials: 'include' });
}
